/*
 * Twilight sky + stars.
 *
 * The sky is rebuilt each frame from a 5-stop vertical gradient. The stops
 * themselves come from a per-phase preset (one preset per WorldPhase). At a
 * phase boundary we lerp the corresponding stops between the current and next
 * preset, so the sky breathes between four named looks rather than snapping.
 *
 * Integration choice: this module *pulls* WorldState from `state/world` rather
 * than accepting it as an argument. That keeps the existing `update(t)`
 * signature stable so game.ts doesn't need to thread the world state through
 * every render module. World state mutation goes through `tickWorld()` in the
 * game loop before any render `update` runs, so by the time we read here, the
 * state is fresh for this frame.
 */

import { Container, FillGradient, Graphics } from 'pixi.js';
import { PALETTE_HEX, WORLD, hexToNum } from '@/render/palette';
import { currentWorldState, type WorldPhase, type WorldState } from '@/state/world';

const STAR_POSITIONS: ReadonlyArray<readonly [number, number]> = [
  [80, 40], [150, 70], [220, 30], [310, 55], [400, 40], [480, 80],
  [540, 35], [100, 110], [260, 100], [380, 90], [500, 120],
];

/**
 * One 5-stop gradient preset per phase. Stops are at the fixed offsets used by
 * the v0.2 sky (0, 0.25, 0.5, 0.65, 1) so the canonical zenith→ground ladder
 * stays intact — only the hex values shift.
 *
 * Recipe per phase:
 *   - "dusk" is the v0.2 canonical look (matches sky.ts before TASK-10).
 *   - "deeptwilight" deepens the upper sky and pulls the horizon haze cooler.
 *   - "late" is the darkest — both upper stops drop toward twilightDeep, the
 *     horizon haze loses its blue cast, and the ground green deepens.
 *   - "dawn" pulls the zenith and upper sky a hair brighter (more twilight,
 *     less deep) — never warm enough to break the "always evening" rule.
 */
const GRADIENT_PRESETS: Record<WorldPhase, ReadonlyArray<readonly [number, string]>> = {
  dusk: [
    [0.0, PALETTE_HEX.twilightDeep],
    [0.25, PALETTE_HEX.twilight],
    [0.5, PALETTE_HEX.twilightMid],
    [0.65, PALETTE_HEX.mossMid],
    [1.0, PALETTE_HEX.mossDeep],
  ],
  deeptwilight: [
    [0.0, '#1f1538'],
    [0.25, '#3a2f5c'],
    [0.5, '#4e5070'],
    [0.65, '#345c44'],
    [1.0, '#243f31'],
  ],
  late: [
    [0.0, '#160e2a'],
    [0.25, '#2a2148'],
    [0.5, '#3e4061'],
    [0.65, '#2a4d38'],
    [1.0, '#1c3326'],
  ],
  dawn: [
    [0.0, '#2f2354'],
    [0.25, '#544478'],
    [0.5, '#646684'],
    [0.65, '#447a59'],
    [1.0, '#345840'],
  ],
};

const PHASE_ORDER: ReadonlyArray<WorldPhase> = ['dusk', 'deeptwilight', 'late', 'dawn'];

function nextPhaseOf(p: WorldPhase): WorldPhase {
  const idx = PHASE_ORDER.indexOf(p);
  return PHASE_ORDER[(idx + 1) % PHASE_ORDER.length]!;
}

/** Blend hex strings #rrggbb component-wise (gamma-naive — fine for twilight tones). */
function lerpHex(a: string, b: string, t: number): string {
  const ah = a.replace('#', '');
  const bh = b.replace('#', '');
  const ar = parseInt(ah.slice(0, 2), 16);
  const ag = parseInt(ah.slice(2, 4), 16);
  const ab = parseInt(ah.slice(4, 6), 16);
  const br = parseInt(bh.slice(0, 2), 16);
  const bg = parseInt(bh.slice(2, 4), 16);
  const bb = parseInt(bh.slice(4, 6), 16);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bch = Math.round(ab + (bb - ab) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bch.toString(16).padStart(2, '0')}`;
}

/**
 * Blend factor between the current phase and the next phase. We start cross
 * fading at phaseT=0.5 and finish at phaseT=1.0, so each phase dwells at its
 * own pure look for the first half and gradually hands off across the second.
 * This is what makes the cycle feel "breathed" rather than "stepped."
 */
function crossfadeAmount(phaseT: number): number {
  if (phaseT <= 0.5) return 0;
  return (phaseT - 0.5) / 0.5;
}

/**
 * Compose the current 5 stops by lerping the per-phase preset against the next
 * one. Returns the same shape as a gradient preset.
 */
function currentStops(state: WorldState): ReadonlyArray<readonly [number, string]> {
  const cur = GRADIENT_PRESETS[state.phase];
  const nxt = GRADIENT_PRESETS[nextPhaseOf(state.phase)];
  const blend = crossfadeAmount(state.phaseT);
  if (blend <= 0) return cur;
  return cur.map(([offset, hex], i) => {
    const nxtStop = nxt[i]!;
    return [offset, lerpHex(hex, nxtStop[1], blend)] as const;
  });
}

export interface SkyView {
  container: Container;
  update(t: number): void;
}

export function createSky(): SkyView {
  const root = new Container();

  // The sky gradient is rebuilt each frame (it's a single rect, cheap). We
  // keep one Graphics object and clear+refill it.
  const skyGfx = new Graphics();
  root.addChild(skyGfx);

  // Distant hills silhouette. Static — colored from canonical twilightDeep.
  const hills = new Graphics();
  hills
    .moveTo(0, 180)
    .quadraticCurveTo(120, 130, 240, 170)
    .quadraticCurveTo(360, 210, 480, 160)
    .quadraticCurveTo(560, 130, 600, 175)
    .lineTo(600, 220)
    .lineTo(0, 220)
    .closePath()
    .fill({ color: hexToNum(PALETTE_HEX.twilightDeep), alpha: 0.55 });
  root.addChild(hills);

  // Grass speckle — drawn once, deterministic positions matching v0.2.
  const grass = new Graphics();
  for (let i = 0; i < 120; i++) {
    const gx = (i * 73) % WORLD.width;
    const gy = 230 + ((i * 127) % (WORLD.height - 230));
    grass.rect(gx, gy, 2, 1).fill({ color: 0xffffff, alpha: 0.045 });
  }
  root.addChild(grass);

  // Stars — redrawn each frame to twinkle.
  const stars = new Graphics();
  root.addChild(stars);

  let lastStopsKey = '';

  function rebuildSky(state: WorldState): void {
    const stops = currentStops(state);
    // Cheap memo: only rebuild the gradient when its stop colors actually
    // change. The check uses the joined-string of stops since FillGradient
    // doesn't expose its color array for diffing.
    const key = stops.map(([o, c]) => `${o}:${c}`).join('|');
    if (key === lastStopsKey) return;
    lastStopsKey = key;

    skyGfx.clear();
    const gradient = new FillGradient(0, 0, 0, WORLD.height);
    for (const [offset, hex] of stops) {
      gradient.addColorStop(offset, hexToNum(hex));
    }
    skyGfx.rect(0, 0, WORLD.width, WORLD.height).fill(gradient);
  }

  function update(t: number): void {
    const state = currentWorldState();
    rebuildSky(state);

    // Stars get a slight visibility bump at deeper phases — the sky is darker
    // so they read more strongly without us turning up brightness.
    const visibilityBoost = state.phase === 'late' ? 1.15 : state.phase === 'deeptwilight' ? 1.08 : 1.0;
    // Drizzle dims stars (clouds covering them).
    const weatherDim = 1 - state.weatherT * 0.6;
    stars.clear();
    STAR_POSITIONS.forEach(([sx, sy], i) => {
      const tw = 0.5 + 0.5 * Math.sin(t * 0.002 + i);
      const alpha = Math.min(1, (0.4 + 0.6 * tw) * visibilityBoost * weatherDim);
      stars.circle(sx, sy, 1.2).fill({ color: 0xffffff, alpha });
    });
  }

  return { container: root, update };
}
